import React, { useState, useRef, useEffect } from "react";
import { Icon } from "@iconify/react";
import axios from "axios";
import {
  getEducationMissingFields,
  getTestMissingFields,
  getStoryMissingFields,
  isActivityComplete,
  isEducationComplete,
  isTestComplete,
  isStoryComplete,
  PATHFINDER_SYSTEM_PROMPT,
} from "./helpers";
import { user_profile_info } from "../../Redux/ProfileAction";

import { BACKEND_URL } from "../../CommonApi/CommonServices";
import { useDispatch, useSelector } from "react-redux";
import PricingModal from "../Modals/PricingModal";
import { useNavigate } from "react-router";
const BASE_URL = BACKEND_URL;
const localStorageToken = localStorage.getItem("token") || "";

export default function PathFinderAgent({ popup, onCloseOpenModal }) {
  const [message, setMessage] = useState("");
  const [isConnected, setIsConnected] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [isMuted, setIsMuted] = useState(false);
  const [agentTokens, setAgentTokens] = useState(0);
  const [isAgentSpeaking, setIsAgentSpeaking] = useState(false);

  const [education, setEducation] = useState({ completed: false });
  const [test, setTest] = useState({ completed: false });
  const [activities, setActivities] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [story, setStory] = useState(null);
  const peerConnection = useRef(null);
  const dataChannel = useRef(null);
  const responseTimer = useRef(null);
  const errorTimer = useRef(null);
  const currentMessageId = useRef(null);
  const activeAudioStream = useRef(null);
  const audioContext = useRef(null);
  const connectionStabilized = useRef(false);
  const greetingSent = useRef(false);
  const conversationLog = useRef([]);
  const conversationTokens = useRef(0);
  const partialTranscript = useRef("");
  const tokenSelector = useSelector((state) => state.rootReducer?.user?.token);
  const getProfile = useSelector((state) => state.profileReducer);
  const history = useNavigate();

  const token = tokenSelector || localStorageToken;
  const dispatch = useDispatch();

  useEffect(() => {
    loadUserData();

    return () => {
      disconnect();
    };
  }, []);

  const loadEducation = async () => {
    try {
      const savedEducation = await axios.get(`${BASE_URL}education`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (savedEducation?.data?.education) {
        const eduData = savedEducation.data.education;
        setEducation({
          grade: eduData.grade,
          gpa: eduData.gpa,
          collage_name: eduData.collage_name,
          class_rank: eduData.class_rank,
          completed: eduData.completed,
        });
      }
    } catch (error) {
      console.error("Error loading education data:", error);
    }
  };

  const loadTest = async () => {
    try {
      const savedTest = await axios.get(`${BASE_URL}tests/highest`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (savedTest?.data?.test_score) {
        const testData = savedTest.data.test_score;
        setTest({
          id: testData.id,
          type: testData.type,
          math: testData.math,
          reading_writing: testData.reading_writing,
          total: testData.total,
          test_date: testData.test_date,
          completed: testData.completed,
        });
      }
    } catch (error) {
      console.error("Error loading test data:", error);
    }
  };

  const loadActivities = async () => {
    try {
      const savedActivities = await axios.get(`${BASE_URL}activities-answers`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (savedActivities.data?.data) {
        setActivities(
          savedActivities.data.data.map((activity) => ({
            question_id: activity.question_id,
            name: activity.name,
            grades: activity.grades,
            weeks: activity.weeks,
            hours: activity.hours,
            completed: activity.completed || isActivityComplete(activity),
          }))
        );
      }
    } catch (error) {
      console.error("Error loading activities data:", error);
    }
  };

  const loadStories = async () => {
    try {
      const savedStory = await axios.get(`${BASE_URL}stories/answers`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (Array.isArray(savedStory.data.answers)) {
        const answers = savedStory.data.answers;

        const allAnswersFilled = answers.length === 5;

        setStory({
          answer1: answers[0]?.answer || "",
          answer2: answers[1]?.answer || "",
          answer3: answers[2]?.answer || "",
          answer4: answers[3]?.answer || "",
          answer5: answers[4]?.answer || "",
          completed: allAnswersFilled,
        });
      } else {
        setStory({
          answer1: "",
          answer2: "",
          answer3: "",
          answer4: "",
          answer5: "",
          completed: false,
        });
      }
    } catch (error) {
      console.error("Error loading stories data:", error);
    }
  };

  const loadTokens = async () => {
    try {
      const response = await axios.get(`${BASE_URL}get_token_info`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      console.log("Token Info:", response.data);
      if (response?.data) {
        const tokens = response.data.tokens;
        if (tokens?.remaining_tokens <= 0) {
          if (onCloseOpenModal) {
            onCloseOpenModal();
          } else {
            setShowModal(true);
          }
        } else {
          setAgentTokens(tokens);
        }
      }
    } catch (error) {
      console.error("Error in userConversationSummary:", error);
    }
  };

  const loadUserData = async () => {
    try {
      loadEducation();
      loadTest();
      loadActivities();
      loadStories();
      loadTokens();
    } catch (error) {
      showTemporaryError("Failed to load existing data");
    }
  };

  const buildInitialContext = () => {
    const educationComplete = isEducationComplete(education);
    const testComplete = isTestComplete(test);
    const storyComplete = isStoryComplete(story) || false;
    const hasActivities = activities.length > 0;
    const completeActivities = activities.filter((a) =>
      isActivityComplete(a)
    ).length;

    let context = "=== CURRENT USER DATA STATUS ===\n\n";

    if (educationComplete) {
      context += `✅ EDUCATION: COMPLETE\n${JSON.stringify(
        education,
        null,
        2
      )}\n\n`;
    } else {
      const hasPartialEducation = Object.keys(education).some(
        (key) => key !== "completed" && education[key]
      );
      if (hasPartialEducation) {
        context += `⚠️ EDUCATION: INCOMPLETE (Priority: Complete this first!)\n`;
        context += `Current data: ${JSON.stringify(education, null, 2)}\n`;
        context += `Missing fields: ${getEducationMissingFields(education).join(
          ", "
        )}\n\n`;
      } else {
        context += `❌ EDUCATION: NOT STARTED (Priority: Start here!)\n\n`;
      }
    }

    if (testComplete) {
      context += `✅ TEST: COMPLETE\n${JSON.stringify(test, null, 2)}\n\n`;
    } else {
      const hasPartialTest = Object.keys(test).some(
        (key) => key !== "completed" && test[key]
      );
      if (hasPartialTest) {
        context += `⚠️ TEST: INCOMPLETE\n`;
        context += `Current data: ${JSON.stringify(test, null, 2)}\n`;
        context += `Missing fields: ${getTestMissingFields(test).join(
          ", "
        )}\n\n`;
      } else {
        context += `❌ TEST: NOT STARTED\n\n`;
      }
    }

    if (storyComplete) {
      context += `✅ STORY: COMPLETE\n${JSON.stringify(story, null, 2)}\n\n`;
    } else {
      const hasPartialStory = Object.keys(story).some(
        (key) => key !== "completed" && story[key]
      );
      if (hasPartialStory) {
        context += `⚠️ STORY: INCOMPLETE\n`;
        context += `Current data: ${JSON.stringify(story, null, 2)}\n`;
        context += `Missing fields: ${getStoryMissingFields(story).join(
          ", "
        )}\n\n`;
      } else {
        context += `❌ STORY: NOT STARTED\n\n`;
      }
    }

    if (activities.length > 0) {
      context += `📋 ACTIVITIES: ${activities.length} total (${completeActivities} complete)\n`;
      context += `\n🚨 CRITICAL FOR UPDATES: Current Activities with question_ids:\n`;
      activities.forEach((activity, index) => {
        const complete = isActivityComplete(activity);
        context += `${complete ? "✅" : "⚠️"} Activity ${index + 1}:\n`;
        context += `   - Name: "${activity.name || "Unnamed"}"\n`;
        context += `   - Question ID: "${activity.question_id}" (USE THIS FOR UPDATES!)\n`;
        context += `   - Grades: "${activity.grades || "Not set"}"\n`;
        context += `   - Weeks: "${activity.weeks || "Not set"}"\n`;
        context += `   - Hours: "${activity.hours || "Not set"}"\n`;
        if (!complete) {
          const missing = [];
          if (!activity.name) missing.push("name");
          if (!activity.grades) missing.push("grades");
          if (!activity.weeks) missing.push("weeks per year");
          if (!activity.hours) missing.push("hours per week");
          context += `   - Missing: ${missing.join(", ")}\n`;
        }
        context += `\n`;
      });

      context += `\n🔄 ACTIVITY UPDATE INSTRUCTIONS:\n`;
      context += `When user says "update my [activity name]" or "change my [activity name]":\n`;
      context += `1. Find the activity by name in the list above\n`;
      context += `2. Use the EXACT question_id from the list\n`;
      context += `3. Example: "update my soccer activity" → use question_id: "${
        activities.find((a) => a.name?.toLowerCase().includes("soccer"))
          ?.question_id || "FIND_CORRECT_ID"
      }"\n`;
      context += `4. Only include the fields being changed\n\n`;
    } else {
      context += `❌ ACTIVITIES: None recorded\n\n`;
    }

    context += "=== ACTION REQUIRED ===\n";
    if (!educationComplete) {
      context += "🎯 PRIMARY FOCUS: Complete Education section first!\n";
      const missing = getEducationMissingFields(education);
      if (missing.length > 0) {
        context += `Ask for: ${missing.join(", ")}\n`;
      }
    } else if (!testComplete) {
      context += "🎯 PRIMARY FOCUS: Complete Test section!\n";
      const missing = getTestMissingFields(test);
      if (missing.length > 0) {
        context += `Ask for: ${missing.join(", ")}\n`;
      }
    } else if (!hasActivities) {
      context += "🎯 PRIMARY FOCUS: Add at least one Activity!\n";
    } else if (completeActivities < activities.length) {
      context += "🎯 PRIMARY FOCUS: Complete incomplete activities!\n";
    } else {
      context +=
        "🎯 ALL CORE DATA COMPLETE: Ask about new activities or updates\n";
    }

    return context;
  };

  const sendInitialGreeting = () => {
    if (dataChannel.current?.readyState === "open" && !greetingSent.current) {
      greetingSent.current = true;
      const context = buildInitialContext();

      dataChannel.current.send(
        JSON.stringify({
          type: "conversation.item.create",
          item: {
            type: "message",
            role: "system",
            content: [
              {
                type: "input_text",
                text: `${context}

🚨 CRITICAL REMINDER: SAVE DATA IMMEDIATELY when user provides ANY information!

Example workflow:
1. User says: "I'm a junior with a 3.5 GPA"
2. IMMEDIATELY call save_education({grade: "junior", gpa: "3.5"})
3. WAIT for function to complete 
4. THEN respond: "Great! I've noted you're a junior with a 3.5 GPA. Now I need your current year, college name, and class rank."

NEVER claim to have saved data unless you actually called the save function. Start conversation by greeting and asking for the priority missing information.`,
              },
            ],
          },
        })
      );

      dataChannel.current.send(JSON.stringify({ type: "response.create" }));
    }
  };

  const initializeWebRTC = async () => {
    try {
      if (peerConnection.current) {
        peerConnection.current.close();
      }
      cleanupAudio();

      const pc = new RTCPeerConnection({
        iceServers: [{ urls: "stun:stun.l.google.com:19302" }],
      });

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: 24000,
        },
      });
      activeAudioStream.current = stream;
      stream.getTracks().forEach((track) => pc.addTrack(track, stream));

      const channel = pc.createDataChannel("oai-events");
      channel.onmessage = handleDataMessage;
      channel.onopen = () => {
        console.log("Data channel opened");

        setTimeout(() => {
          connectionStabilized.current = true;
          sendInitialGreeting();
        }, 2000);
      };
      dataChannel.current = channel;

      pc.ontrack = (event) => {
        if (event.track.kind === "audio") {
          const audioElement = new Audio();
          audioElement.srcObject = event.streams[0];
          audioElement.autoplay = true;
        }
      };

      pc.onconnectionstatechange = () => {
        console.log("Connection state:", pc.connectionState);
        if (pc.connectionState === "failed") {
          showTemporaryError("Connection failed. Please try reconnecting.");
          disconnect();
        }
      };

      return pc;
    } catch (err) {
      showTemporaryError("Microphone access required");
      console.error("WebRTC init error:", err);
      throw err;
    }
  };

  const handleDataMessage = async (event) => {
    try {
      const message = JSON.parse(event.data);
      console.log("Received message:", message.type);

      switch (message.type) {
        case "session.created":
        case "session.updated":
          console.log("✅ Session configured");
          break;

        case "input_audio_buffer.speech_started":
          console.log("🎤 Listening...");
          break;

        case "input_audio_buffer.speech_stopped":
          console.log("⌛ Processing...");
          break;

        case "conversation.item.created": {
          if (
            message.item?.type === "message" &&
            message.item.role === "user"
          ) {
            const audioContent = message.item.content?.find(
              (c) => c.type === "input_audio"
            );
            if (audioContent?.transcript) {
              console.log("👤 Student:", audioContent.transcript);
              conversationLog.current.push(
                `Student: ${audioContent.transcript}`
              );
              showTemporaryMessage({
                id: `user_${message.item.id}`,
                text: audioContent.transcript,
                isFinal: true,
                type: "user",
                partial: false,
              });
            }
          }
          break;
        }

        case "response.function_call_arguments.done":
          await handleFunctionCall({ message });
          break;

        case "response.audio_transcript.delta":
          if (message.delta) {
            partialTranscript.current += message.delta;
            setIsAgentSpeaking(true); // Show that agent is speaking
          }
          break;

        case "response.audio_transcript.done":
          if (message.transcript) {
            console.log("🤖 PathFinder:", message.transcript);
            conversationLog.current.push(`PathFinder: ${message.transcript}`);
            partialTranscript.current = "";
            setIsAgentSpeaking(false);
            showFinalMessage({
              id: `assistant_${message.item_id}`,
              text: message.transcript,
              type: "assistant",
            });
          }
          break;

        case "response.done":
          setIsAgentSpeaking(false);
          partialTranscript.current = "";
          if (message.response?.usage?.total_tokens) {
            const tokensUsed = message.response.usage.total_tokens;
            conversationTokens.current += tokensUsed;

            const remainingAfterThisResponse =
              agentTokens.remaining_tokens - conversationTokens.current;

            if (remainingAfterThisResponse <= 0) {
              showTemporaryError("Token limit reached. Disconnecting...");
              setTimeout(() => disconnect(), 2000);
            }
          }
          break;

        case "error":
          console.error("❌ Error:", message.error);
          showTemporaryError(message.error.message || "An error occurred");
          break;
      }
    } catch (err) {
      console.error("Message handling error:", err);
    }
  };

  const handleFunctionCall = async ({ message }) => {
    try {
      const args = JSON.parse(message.arguments);
      let result;

      switch (message.name) {
        case "save_education": {
          console.log("💾 Saving education to database:", args);
          result = await saveEducationToDatabase(args);
          break;
        }

        case "save_test": {
          console.log("💾 Saving test to database:", args);
          result = await saveTestToDatabase(args);
          break;
        }

        case "save_story": {
          console.log("💾 Saving story to database:", args);
          result = await saveStoryToDatabase(args);
          break;
        }

        case "save_activity": {
          console.log("💾 Saving activity to database:", args);
          result = await saveActivityToDatabase(args);
          break;
        }

        case "delete_activity": {
          console.log("🗑️ Deleting activity from database:", args);
          result = await deleteActivityFromDatabase(args.question_id);
          break;
        }

        default:
          result = { error: "Unknown function" };
      }

      if (dataChannel.current?.readyState === "open") {
        dataChannel.current.send(
          JSON.stringify({
            type: "conversation.item.create",
            item: {
              type: "function_call_output",
              call_id: message.call_id,
              output: JSON.stringify(result),
            },
          })
        );

        dataChannel.current.send(JSON.stringify({ type: "response.create" }));
      }
    } catch (error) {
      console.error("Function call error:", error);
      if (dataChannel.current?.readyState === "open") {
        dataChannel.current.send(
          JSON.stringify({
            type: "conversation.item.create",
            item: {
              type: "function_call_output",
              call_id: message.call_id,
              output: JSON.stringify({ error: "Failed to execute function" }),
            },
          })
        );
      }
    }
  };

  const connect = async () => {
    try {
      setIsProcessing(true);
      setError(null);
      if (errorTimer.current) {
        clearTimeout(errorTimer.current);
        errorTimer.current = null;
      }
      connectionStabilized.current = false;
      greetingSent.current = false;
      conversationLog.current = [];
      conversationTokens.current = 0;

      peerConnection.current = await initializeWebRTC();
      const sessionResponse = await fetch(
        `${BASE_URL}/openai/realtime/session`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            model: "gpt-4o-mini-realtime-preview",
            modalities: ["audio", "text"],
            voice: "sage",
            input_audio_format: "pcm16",
            output_audio_format: "pcm16",
            input_audio_transcription: {
              model: "whisper-1",
            },
            turn_detection: {
              type: "server_vad",
              threshold: 0.6,
              prefix_padding_ms: 300,
              silence_duration_ms: 500,
            },
            instructions: `${PATHFINDER_SYSTEM_PROMPT}

**🚨 CRITICAL MANDATORY BEHAVIOR - FOLLOW EXACTLY:**

1. **IMMEDIATE DATA EXTRACTION & SAVING:**
   - The MOMENT user mentions ANY piece of information, EXTRACT it and SAVE it IMMEDIATELY
   - NEVER wait for complete information - save partial data RIGHT AWAY
   - Do NOT ask "Should I save this?" - JUST SAVE IT
   - Examples:
     * User: "I'm a junior" → IMMEDIATELY call save_education({grade: "junior"})
     * User: "My GPA is 3.5" → IMMEDIATELY call save_education({cgpa: "3.5"})
     * User: "I got 650 on SAT math" → IMMEDIATELY call save_test({type: "sat", math: "650"})
     * User: "I play basketball for 10 hours" → IMMEDIATELY call save_activity({name: "basketball", hours: "10"})

2. **NEVER CLAIM TO SAVE WITHOUT ACTUALLY SAVING:**
   - NEVER say "I've saved", "I've recorded", "I've noted" unless you ACTUALLY called the save function
   - If you haven't called a save function, you HAVEN'T saved anything
   - Always call the function FIRST, then acknowledge what was saved

3. **WORKFLOW FOR EVERY MESSAGE:**
   Step 1: Listen to user input
   Step 2: Extract ANY data (even partial)
   Step 3: If data found → IMMEDIATELY call appropriate save function
   Step 4: Wait for save to complete
   Step 5: THEN respond acknowledging what was saved and ask for missing fields

**VALIDATION RULES - ALWAYS ENFORCE BEFORE SAVING:**
- CGPA: Must be between 1.0 and 4.0
- Class Rank: Must be between 1 and 50  
- SAT Scores: Math/Reading_Writing (200-800), Total (400-1600)
- PSAT Scores: Math/Reading_Writing (160-760), Total (320-1520)
- Activity weeks per year: Must be 1-52
- Activity hours per week: Must be 1-168

**RESPONSE PATTERN:**
❌ WRONG: "Great! I've noted you're a junior with a 3.5 GPA. Now tell me your college name."
✅ CORRECT: [FIRST call save_education({grade: "junior", cgpa: "3.5"})] THEN say "Perfect! I just saved that you're a junior with a 3.5 GPA. Now I need your current year, college name, and class rank to complete your education profile."

**REMEMBER:** You MUST call save functions immediately when data is provided. Do not discuss, do not hesitate, do not ask permission - JUST SAVE THE DATA FIRST, then respond.`,
            temperature: 0.7,
            max_response_output_tokens: "inf",
            tools: [
              {
                type: "function",
                name: "save_education",
                description:
                  "🚨 CRITICAL: Call this function IMMEDIATELY when user provides ANY education information - even just one field. NEVER claim to have saved education data unless you call this function. Validate: CGPA 1.0-4.0, Class rank 1-50.",
                parameters: {
                  type: "object",
                  properties: {
                    grade: {
                      type: "string",
                      enum: ["freshman", "sophomore", "junior", "senior"],
                      description: "Student's current grade level",
                    },
                    gpa: {
                      type: "string",
                      description:
                        "CGPA between 1.0 and 4.0 (e.g., '3.5', '2.6', '4.0'). Must validate range before saving.",
                    },
                    collage_name: {
                      type: "string",
                      description: "Name of the college/university",
                    },
                    class_rank: {
                      type: "string",
                      description:
                        "Class rank between 1 and 50. Must validate range before saving.",
                    },
                  },
                  additionalProperties: false,
                },
              },
              {
                type: "function",
                name: "save_test",
                description:
                  "🚨 CRITICAL: Call this function IMMEDIATELY when user provides ANY test information - even just one score. NEVER claim to have saved test data unless you call this function. Validate score ranges for SAT/PSAT.",
                parameters: {
                  type: "object",
                  properties: {
                    type: {
                      type: "string",
                      enum: ["sat", "psat"],
                      description: "Type of standardized test",
                    },
                    math: {
                      type: "string",
                      description:
                        "Math score: SAT (200-800), PSAT (160-760). Must validate range before saving.",
                    },
                    reading_writing: {
                      type: "string",
                      description:
                        "Reading/Writing score: SAT (200-800), PSAT (160-760). Must validate range before saving.",
                    },
                    total: {
                      type: "string",
                      description:
                        "Total score: SAT (400-1600), PSAT (320-1520). Must validate range before saving.",
                    },
                    test_date: {
                      type: "string",
                      description:
                        "Test date in format YYYY/MM/DD or YYYY-MM-DD",
                    },
                  },
                  additionalProperties: false,
                },
              },
              {
                type: "function",
                name: "save_activity",
                description:
                  "🚨 CRITICAL: Call this function IMMEDIATELY when user provides ANY activity information - even just the activity name. NEVER claim to have saved activity data unless you call this function. Validate: weeks 1-52, hours 1-168.",
                parameters: {
                  type: "object",
                  properties: {
                    question_id: {
                      type: "string",
                      description:
                        "Unique identifier for the activity (auto-generated if not provided)",
                    },
                    name: {
                      type: "string",
                      description: "Name of the extracurricular activity",
                    },
                    grades: {
                      type: "string",
                      description:
                        "Grades involved (e.g., '9,10,11' or 'freshman,sophomore,junior')",
                    },
                    weeks: {
                      type: "string",
                      description:
                        "Number of weeks per year (1-52). Must validate range before saving.",
                    },
                    hours: {
                      type: "string",
                      description:
                        "Hours per week (1-168, max 24*7=168). Must validate range before saving.",
                    },
                  },
                  additionalProperties: false,
                },
              },
              {
                type: "function",
                name: "delete_activity",
                description:
                  "if user asks delete my basketball activity, use the question_id of the activity to delete it.",
                parameters: {
                  type: "object",
                  properties: {
                    question_id: {
                      type: "string",
                      description:
                        "Unique identifier of the activity to delete",
                    },
                  },
                  required: ["question_id"],
                  additionalProperties: false,
                },
              },
            ],
            tool_choice: "auto",
          }),
        }
      );

      if (!sessionResponse.ok) {
        const errorText = await sessionResponse.text();
        console.error("Session creation failed:", {
          status: sessionResponse.status,
          statusText: sessionResponse.statusText,
          error: errorText,
        });
        throw new Error(
          `Session setup failed: ${sessionResponse.status} ${sessionResponse.statusText}`
        );
      }

      const sessionData = await sessionResponse.json();
      const clientSecret = sessionData.client_secret.value;

      const offer = await peerConnection.current.createOffer({
        offerToReceiveAudio: true,
        offerToReceiveVideo: false,
      });

      await peerConnection.current.setLocalDescription(offer);

      const sdpResponse = await fetch(`${BASE_URL}/openai/realtime/sdp`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          clientSecret: clientSecret,
          sdp: offer.sdp,
        }),
      });

      if (!sdpResponse.ok) {
        const errorText = await sdpResponse.text();
        console.error("SDP exchange failed:", {
          status: sdpResponse.status,
          statusText: sdpResponse.statusText,
          error: errorText,
        });
        throw new Error(
          `SDP exchange failed: ${sdpResponse.status} ${sdpResponse.statusText}`
        );
      }

      const answerSdp = await sdpResponse.text();
      await peerConnection.current.setRemoteDescription({
        type: "answer",
        sdp: answerSdp,
      });

      setIsConnected(true);
    } catch (err) {
      if (err instanceof Error) {
        showTemporaryError(`Connection failed: ${err.message}`);
      } else {
        showTemporaryError("Connection failed: Unknown error");
      }
      disconnect();
    } finally {
      setIsProcessing(false);
    }
  };

  const showFinalMessage = (messageData) => {
    if (responseTimer.current) {
      clearTimeout(responseTimer.current);
    }

    setMessage(messageData.text);
    currentMessageId.current = messageData.id;

    responseTimer.current = setTimeout(() => {
      setMessage("");
      currentMessageId.current = null;
    }, 12000);
  };

  const cleanupAudio = () => {
    if (activeAudioStream.current) {
      activeAudioStream.current.getTracks().forEach((track) => {
        track.stop();
        track.enabled = false;
      });
      activeAudioStream.current = null;
    }
    if (audioContext.current && audioContext.current.state !== "closed") {
      audioContext.current.close();
      audioContext.current = null;
    }
  };

  const showTemporaryError = (errorMessage) => {
    if (errorTimer.current) {
      clearTimeout(errorTimer.current);
    }

    setError(errorMessage);
    errorTimer.current = setTimeout(() => {
      setError(null);
      errorTimer.current = null;
    }, 5000);
  };

  const showTemporaryMessage = (msg) => {
    if (responseTimer.current) {
      clearTimeout(responseTimer.current);
    }
    setMessage(msg.text);
    currentMessageId.current = msg.id;
    conversationLog.current.push(msg.text);

    responseTimer.current = setTimeout(() => {
      setMessage("");
    }, 12000);
  };

  const toggleMute = () => {
    if (activeAudioStream.current) {
      const audioTrack = activeAudioStream.current.getAudioTracks()[0];
      if (audioTrack) {
        const newMutedState = !isMuted;
        audioTrack.enabled = !newMutedState;
        setIsMuted(newMutedState);
      }
    }
  };

  const handleMicClick = async () => {
    if (isConnected) {
      setIsProcessing(true);
      await disconnect();
      setIsProcessing(false);
    } else {
      connect();
    }
  };

  const saveEducationToDatabase = async (educationData) => {
    try {
      const updatedEducation = { ...education, ...educationData };

      const response = await axios.post(
        `${BASE_URL}education/save`,
        {
          grade: updatedEducation.grade?.toLowerCase(),
          gpa: updatedEducation.gpa,
          collage_name: updatedEducation.collage_name,
          class_rank: updatedEducation.class_rank,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.status === 200) {
        setEducation(updatedEducation);
        console.log("✅ Education saved successfully:", updatedEducation);
        return { success: true, data: updatedEducation };
      } else {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
    } catch (error) {
      console.error("Error saving education:", error);
      return { error: "Failed to save education data" };
    } finally {
      loadEducation();
    }
  };

  const saveTestToDatabase = async (testData) => {
    try {
      const updatedTest = { ...test, ...testData };
      const fieldsToValidate = Object.keys(testData);

      if (fieldsToValidate.includes("math") && testData.math) {
        const validRange = updatedTest.type === "sat" ? [200, 800] : [160, 760];
        const mathScore = parseInt(testData.math);
        if (mathScore < validRange[0] || mathScore > validRange[1]) {
          return {
            error: `${updatedTest.type?.toUpperCase()} Math score must be between ${
              validRange[0]
            } and ${validRange[1]}`,
          };
        }
      }
      if (
        fieldsToValidate.includes("reading_writing") &&
        testData.reading_writing
      ) {
        const validRange = updatedTest.type === "sat" ? [200, 800] : [160, 760];
        const readingScore = parseInt(testData.reading_writing);
        if (readingScore < validRange[0] || readingScore > validRange[1]) {
          return {
            error: `${updatedTest.type?.toUpperCase()} Reading/Writing score must be between ${
              validRange[0]
            } and ${validRange[1]}`,
          };
        }
      }

      if (fieldsToValidate.includes("total") && testData.total) {
        const validRange =
          updatedTest.type === "sat" ? [400, 1600] : [320, 1520];
        const totalScore = parseInt(testData.total);
        if (totalScore < validRange[0] || totalScore > validRange[1]) {
          return {
            error: `${updatedTest.type?.toUpperCase()} Total score must be between ${
              validRange[0]
            } and ${validRange[1]}`,
          };
        }
      }

      updatedTest.completed = isTestComplete(updatedTest);
      const response = await axios.post(
        `${BASE_URL}tests/save`,
        {
          id: updatedTest.id,
          type: updatedTest.type?.toLowerCase(),
          math: updatedTest.math,
          reading_writing: updatedTest.reading_writing,
          total: updatedTest.total,
          test_date: updatedTest.test_date,
          completed: updatedTest.completed,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.status === 200) {
        setTest(updatedTest);
        console.log("✅ Test saved successfully:", updatedTest);
        return { success: true, data: updatedTest };
      } else {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
    } catch (error) {
      console.error("Error saving test:", error);
      return { error: "Failed to save test data" };
    } finally {
      loadTest();
    }
  };

  const saveStoryToDatabase = async (storyInput) => {
    try {
      const updatedStory = { ...story };

      if (storyInput.id === "1") {
        updatedStory.answer1 = storyInput.answer;
      } else if (storyInput.id === "2") {
        updatedStory.answer2 = storyInput.answer;
      } else if (storyInput.id === "3") {
        updatedStory.answer3 = storyInput.answer;
      } else if (storyInput.id === "4") {
        updatedStory.answer4 = storyInput.answer;
      } else if (storyInput.id === "5") {
        updatedStory.answer5 = storyInput.answer;
      }

      updatedStory.completed = isStoryComplete(updatedStory);

      const response = await axios.post(
        `${BASE_URL}story/answer/save`,
        {
          story_id: storyInput.id,
          response: storyInput.answer,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.status === 200) {
        setStory(updatedStory);
        console.log("✅ Story saved successfully:", updatedStory);
        return { success: true, data: updatedStory };
      } else {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
    } catch (error) {
      console.error("Error saving story:", error);
      return { error: "Failed to save story data" };
    } finally {
      loadStories();
    }
  };

  const sendUpdatedContext = () => {
    if (
      dataChannel.current?.readyState === "open" &&
      connectionStabilized.current
    ) {
      const context = buildInitialContext();

      dataChannel.current.send(
        JSON.stringify({
          type: "conversation.item.create",
          item: {
            type: "message",
            role: "system",
            content: [
              {
                type: "input_text",
                text: `🔄 UPDATED CONTEXT:\n\n${context}\n\n🚨 REMEMBER: When updating activities, use the EXACT question_id from the context above!`,
              },
            ],
          },
        })
      );
    }
  };

  const saveActivityToDatabase = async (activityData) => {
    try {
      const fieldsToValidate = Object.keys(activityData);

      if (fieldsToValidate.includes("weeks") && activityData.weeks) {
        const weeksNum = parseInt(activityData.weeks);
        if (weeksNum < 1 || weeksNum > 52) {
          return { error: "Activity weeks per year must be between 1 and 52" };
        }
      }

      if (fieldsToValidate.includes("hours") && activityData.hours) {
        const hoursNum = parseInt(activityData.hours);
        if (hoursNum < 1 || hoursNum > 168) {
          return { error: "Activity hours per week must be between 1 and 168" };
        }
      }

      if (
        !activityData.question_id &&
        (!activityData.name || activityData.name.trim() === "")
      ) {
        return { error: "Activity name is required for new activities" };
      }

      const isUpdate = !!activityData.question_id;
      let finalQuestionId = activityData.question_id;

      if (isUpdate) {
        const existingActivity = activities.find(
          (a) => a.question_id === activityData.question_id
        );
        if (!existingActivity) {
          return {
            error: `Activity with question_id ${activityData.question_id} not found`,
          };
        }
        console.log(
          `Updating existing activity: ${existingActivity.name} (ID: ${activityData.question_id})`
        );
      }

      if (!isUpdate) {
        let matchedActivity = null;
        try {
          matchedActivity = await matchActivity(activityData.name);
        } catch (error) {
          console.warn("Error matching activity:", error);
        }

        finalQuestionId = matchedActivity
          ? matchedActivity.id
          : String(
              Number(activities?.[activities.length - 1]?.question_id || "3") +
                1
            );
      }

      const payload = {
        question_id: finalQuestionId,
      };

      if (activityData.name) payload.name = activityData.name;
      if (activityData.grades) payload.grades = activityData.grades;
      if (activityData.weeks) payload.weeks = activityData.weeks;
      if (activityData.hours) payload.hours = activityData.hours;

      console.log(`${isUpdate ? "Updating" : "Creating"} activity:`, payload);

      const response = await axios.post(
        `${BASE_URL}activity-answer/save`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.status === 200) {
        if (isUpdate) {
          setActivities((prev) =>
            prev.map((a) => {
              if (a.question_id === activityData.question_id) {
                const updatedActivity = { ...a, ...activityData };
                updatedActivity.completed = isActivityComplete(updatedActivity);
                return updatedActivity;
              }
              return a;
            })
          );
          console.log("✅ Activity updated successfully");
        } else {
          const newActivity = {
            ...activityData,
            question_id: finalQuestionId,
            completed: isActivityComplete(activityData),
          };
          setActivities((prev) => [...prev, newActivity]);
          console.log("✅ Activity created successfully");
        }

        setTimeout(() => {
          sendUpdatedContext();
        }, 1000);

        return {
          success: true,
          data: { ...activityData, question_id: finalQuestionId },
          operation: isUpdate ? "update" : "create",
        };
      } else {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
    } catch (error) {
      console.error("Error saving activity:", error);
      return {
        error: "Failed to save activity data",
        details: error.message,
      };
    } finally {
      loadActivities();
    }
  };

  const matchActivity = async (activityName) => {
    try {
      const response = await axios.post(
        `${BASE_URL}openai/realtime/match_activity`,
        { activity_name: activityName },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      if (response.data?.matched_activity) {
        const activity = response.data.matched_activity;
        return activity;
      }
    } catch (error) {
      console.error("Error in match activity:", error);
    }
  };

  const deleteActivityFromDatabase = async (questionId) => {
    try {
      const response = await axios.post(
        `${BASE_URL}activity-answer/delete`,
        {
          question_id: questionId,
          deleted: true,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.status === 200) {
        setActivities((prev) =>
          prev.filter((a) => a.question_id !== questionId)
        );
        console.log("✅ Activity deleted successfully");
        return { success: true, message: "Activity deleted successfully" };
      } else {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
    } catch (error) {
      console.error("Error deleting activity:", error);
      return { error: "Failed to delete activity" };
    } finally {
      loadActivities();
    }
  };

  const saveTokensToDatabase = async () => {
    try {
      const response = await axios.post(
        `${BASE_URL}save_token_usage`,
        { tokens: conversationTokens.current },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.status === 200) {
        console.log("✅ used token saved successfully");
        return { success: true };
      } else {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
    } catch (error) {
      console.error("Error saving conversation summary:", error);
      return { error: "Failed to save conversation summary" };
    }
  };

  const disconnect = async () => {
    if (conversationTokens.current > 0) {
      saveTokensToDatabase();
    }
    setIsProcessing(false);
    setIsAgentSpeaking(false);
    partialTranscript.current = "";
    setIsConnected(false);
    setMessage("");
    setIsMuted(false);
    setError(null);

    if (responseTimer.current) {
      clearTimeout(responseTimer.current);
      responseTimer.current = null;
    }

    if (errorTimer.current) {
      clearTimeout(errorTimer.current);
      errorTimer.current = null;
    }

    if (peerConnection.current) {
      peerConnection.current.close();
      peerConnection.current = null;
    }

    if (dataChannel.current) {
      dataChannel.current.close();
      dataChannel.current = null;
    }

    cleanupAudio();

    currentMessageId.current = null;
    connectionStabilized.current = false;
    greetingSent.current = false;

    setTimeout(() => {
      conversationLog.current = [];
      conversationTokens.current = 0;
    }, 4000);

    if (responseTimer.current) {
      clearTimeout(responseTimer.current);
      responseTimer.current = null;
    }

    if (errorTimer.current) {
      clearTimeout(errorTimer.current);
      errorTimer.current = null;
    }

    console.log(
      `Session ended. Total tokens used: ${conversationTokens.current}`
    );
    await loadTokens();
  };

  const getCompletionStatus = () => {
    const educationComplete = education.completed || false;
    const testComplete = test?.completed || false;
    const storyComplete = story?.completed || false;
    const hasActivities = activities.length > 0;

    const sections = [
      { name: "Education", completed: educationComplete, icon: "mdi:collage" },
      {
        name: "Tests",
        completed: testComplete,
        icon: "mdi:file-document-edit",
      },
      {
        name: "Story",
        completed: storyComplete,
        icon: "mdi:book-open-page-variant",
      },
      { name: "Activities", completed: hasActivities, icon: "mdi:star" },
    ];

    const sortedSections = sections.sort((a, b) => {
      if (a.completed === b.completed) return 0;
      return a.completed ? -1 : 1;
    });

    const completedSections = sections.filter((s) => s.completed).length;

    return {
      total: 4,
      completed: completedSections,
      percentage: Math.round((completedSections / 4) * 100),
      sections: sortedSections,
    };
  };

  const completionStatus = getCompletionStatus();

  const storyQuestions = [
    {
      key: "answer1",
      label: "Biggest Challenge",
    },
    {
      key: "answer2",
      label: "Real Difference",
    },
    {
      key: "answer3",
      label: "Most Adventurous",
    },
    {
      key: "answer4",
      label: "Leadership Moment",
    },
    {
      key: "answer5",
      label: "Future Vision",
    },
  ];

  const formatNumber = (num) => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + "M";
    } else if (num >= 1000) {
      return (num / 1000).toFixed(0) + "K";
    }
    return num.toString();
  };

  const containerStyle = {
    width: "100%",
    minHeight: "auto",
    padding: popup ? "2px" : "20px",
    paddingBottom: popup ? "2px" : "40px",
    display: "flex",
    flexDirection: "column",
    boxSizing: "border-box",
    fontFamily:
      "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  };

  const scrollContainerStyle = {
    width: "100%",
    height: "100%",
    overflowY: "auto",
    overflowX: "hidden",
    WebkitOverflowScrolling: "touch",
    scrollbarWidth: "none",
    msOverflowStyle: "none",
    padding: "0",
    paddingBottom: popup ? "2px" : "40px",
    boxSizing: "border-box",
  };

  const progressBarContainerStyle = {
    background:
      "linear-gradient(135deg, rgba(59, 130, 246, 0.08), rgba(99, 102, 241, 0.08))",
    borderRadius: "16px",
    padding: "10px",
    marginBottom: "24px",
    border: "1px solid rgba(59, 130, 246, 0.15)",
    backdropFilter: "blur(10px)",
    width: "100%",
    boxSizing: "border-box",
  };

  const sectionsGridStyle = {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(60px, 1fr))",
    gap: "2px",
    width: "100%",
  };

  const sectionStyle = {
    textAlign: "center",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    minWidth: "45px",
  };

  const micButtonContainerStyle = {
    position: "relative",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: "16px",
    width: "100%",
    minHeight: popup ? "68px" : "76px",
  };

  const micButtonStyle = {
    width: popup ? "68px" : "76px",
    height: popup ? "68px" : "76px",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    background: isProcessing
      ? "linear-gradient(135deg, #f59e0b, #f97316)"
      : isConnected
      ? "linear-gradient(135deg, #ef4444, #dc2626)"
      : "linear-gradient(135deg, #3b82f6, #6366f1)",
    border: "none",
    cursor: isProcessing ? "not-allowed" : "pointer",
    position: "relative",
  };

  const muteButtonStyle = {
    marginTop: "32px",
    marginLeft: "4px",
    width: popup ? "32px" : "36px",
    height: popup ? "32px" : "36px",
    borderRadius: "50%",
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.2)",
    backgroundColor: isMuted ? "#ef4444" : "#10b981",
    border: "3px solid white",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 2,
    outline: "none",
    WebkitTapHighlightColor: "transparent",
  };

  const connectionStatusStyle = {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    marginTop: "8px",
  };

  const statusDotStyle = {
    width: "8px",
    height: "8px",
    borderRadius: "50%",
    backgroundColor: isConnected ? "#22c55e" : "#6b7280",
    animation: isConnected
      ? "pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite"
      : "none",
  };

  const cardsGridStyle = {
    display: "grid",
    gridTemplateColumns: popup
      ? "repeat(auto-fit, minmax(140px, 1fr))"
      : "repeat(auto-fit, minmax(180px, 1fr))",
    gap: "12px",
    marginBottom: "24px",
  };

  const cardStyle = (gradient, borderColor) => ({
    background: gradient,
    borderRadius: "16px",
    padding: popup ? "16px" : "20px",
    textAlign: "center",
    border: `1px solid ${borderColor}`,
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    backdropFilter: "blur(8px)",
    boxShadow: "0 4px 6px rgba(0, 0, 0, 0.05)",
  });

  const infoSectionStyle = {
    marginTop: "24px",
    marginBottom: "40px",
  };

  const infoGridStyle = {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
    gap: "16px",
    fontSize: "14px",
  };

  const infoCardStyle = (bgColor, borderColor) => ({
    backgroundColor: bgColor,
    borderRadius: "12px",
    padding: "20px",
    border: `1px solid ${borderColor}`,
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
  });

  const tokenDisplayStyle = {
    fontSize: "11px",
    color: "#6b7280",
    marginTop: "8px",
    textAlign: "center",
  };

  return (
    <>
      <div style={containerStyle}>
        <style>
          {`
          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.5; }
          }
          
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
          
          /* Hide scrollbar */
          div::-webkit-scrollbar {
            display: none;
          }
          
          /* Mobile responsiveness */
          @media (max-width: 768px) {
            .info-grid {
              grid-template-columns: 1fr !important;
            }
            
            .cards-grid {
              grid-template-columns: 1fr !important;
            }
            
            .sections-grid {
              grid-template-columns: repeat(4, 1fr) !important;
            }
          }
          
          @media (max-width: 480px) {
            .mic-button {
              width: 60px !important;
              height: 60px !important;
            }
            
            .mute-button {
              width: 28px !important;
              height: 28px !important;
              right: -35px !important;
            }
          }
        `}
        </style>

        <div style={scrollContainerStyle}>
          <div style={progressBarContainerStyle}>
            <div style={sectionsGridStyle} className="sections-grid">
              {completionStatus.sections.map((section, index) => (
                <div key={section.name} style={sectionStyle}>
                  <div
                    style={{
                      width: "20px",
                      height: "20px",
                      borderRadius: "50%",
                      marginBottom: "8px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      transition: "all 0.3s ease",
                      backgroundColor: section.completed
                        ? "#10b981"
                        : "#e5e7eb",
                      border: section.completed
                        ? "2px solid #059669"
                        : "2px solid #d1d5db",
                      boxShadow: section.completed
                        ? "0 4px 8px rgba(16, 185, 129, 0.3)"
                        : "none",
                    }}
                  >
                    <Icon
                      icon={section.completed ? "mdi:check" : section.icon}
                      width="10"
                      height="10"
                      color={section.completed ? "white" : "#6b7280"}
                    />
                  </div>
                  <div
                    style={{
                      fontSize: "10px",
                      fontWeight: "500",
                      color: section.completed ? "#047857" : "#6b7280",
                      lineHeight: "1.2",
                      wordBreak: "break-word",
                    }}
                  >
                    {section.name}
                  </div>
                </div>
              ))}
            </div>
          </div>
          {error && (
            <div
              style={{
                backgroundColor: "rgba(254, 226, 226, 0.8)",
                border: "1px solid rgba(252, 165, 165, 0.5)",
                borderRadius: "12px",
                padding: "16px",
                marginBottom: "24px",
                backdropFilter: "blur(8px)",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                }}
              >
                <Icon
                  icon="mdi:alert-circle"
                  width="20"
                  height="20"
                  color="#ef4444"
                />
                <p
                  style={{
                    color: "#dc2626",
                    fontWeight: "500",
                    margin: "0",
                    fontSize: "14px",
                  }}
                >
                  {error}
                </p>
              </div>
            </div>
          )}
          {message && (
            <div
              style={{
                backgroundColor: "rgba(239, 246, 255, 0.8)",
                borderRadius: "16px",
                padding: "20px",
                marginBottom: "24px",
                border: "1px solid rgba(147, 197, 253, 0.3)",
                backdropFilter: "blur(8px)",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: "12px",
                }}
              >
                <div
                  style={{
                    backgroundColor: "rgba(59, 130, 246, 0.2)",
                    borderRadius: "50%",
                    padding: "8px",
                    marginTop: "2px",
                    flexShrink: 0,
                  }}
                >
                  <Icon
                    icon="mdi:compass"
                    width="16"
                    height="16"
                    color="#3b82f6"
                  />
                </div>
                <p
                  style={{
                    color: "#1f2937",
                    fontSize: popup ? "14px" : "16px",
                    lineHeight: "1.6",
                    margin: "0",
                    wordBreak: "break-word",
                  }}
                >
                  {isAgentSpeaking ? "Processing..." : message}
                </p>
              </div>
            </div>
          )}
          <div
            style={{
              textAlign: "center",
              marginBottom: "32px",
            }}
          >
            <div style={micButtonContainerStyle}>
              <button
                onClick={handleMicClick}
                disabled={
                  isProcessing ||
                  agentTokens === 0 ||
                  agentTokens?.used_tokens >= agentTokens?.total_tokens
                }
                style={micButtonStyle}
                className="mic-button"
                onMouseEnter={(e) => {
                  if (!isProcessing) {
                    e.target.style.transform = "scale(1.05)";
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isProcessing) {
                    e.target.style.transform = "scale(1)";
                  }
                }}
              >
                <Icon
                  icon={
                    isProcessing
                      ? "mdi:loading"
                      : isConnected
                      ? "mdi:phone-hangup"
                      : "mdi:microphone"
                  }
                  width={popup ? "28" : "32"}
                  height={popup ? "28" : "32"}
                  color="white"
                  style={{
                    animation: isProcessing
                      ? "spin 1s linear infinite"
                      : "none",
                  }}
                />
              </button>

              {isConnected && (
                <button
                  onClick={toggleMute}
                  style={muteButtonStyle}
                  className="mute-button"
                  onMouseEnter={(e) => {
                    e.target.style.transform = "scale(1.1)";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.transform = "scale(1)";
                  }}
                >
                  <Icon
                    icon={isMuted ? "mdi:microphone-off" : "mdi:microphone"}
                    width={popup ? "12" : "16"}
                    height={popup ? "12" : "16"}
                    color="white"
                  />
                </button>
              )}
            </div>

            <div>
              <p
                style={{
                  color: "#1f2937",
                  fontSize: popup ? "14px" : "16px",
                  fontWeight: "500",
                  marginBottom: "8px",
                  margin: "0 0 8px 0",
                }}
              >
                {isProcessing
                  ? "Connecting to PathFinder..."
                  : isConnected
                  ? isMuted
                    ? "Connected - Microphone Muted"
                    : "I'm listening... How can I help with your educational journey?"
                  : "Tap to start"}
              </p>

              <div style={connectionStatusStyle}>
                <div style={statusDotStyle} />
                <span
                  style={{
                    color: "#4b5563",
                    fontWeight: "500",
                    fontSize: popup ? "10px" : "11px",
                  }}
                >
                  {isConnected
                    ? "Connected to PathFinder AI"
                    : "Click microphone to begin"}
                </span>
              </div>
            </div>

            {agentTokens !== 0 && (
              <div style={tokenDisplayStyle}>
                <div>
                  {formatNumber(agentTokens?.used_tokens)}/
                  {formatNumber(agentTokens?.total_tokens)} tokens
                </div>
                {agentTokens?.used_tokens >= agentTokens?.total_tokens && (
                  <div style={{ color: "#ef4444", marginTop: "4px" }}>
                    Token limit reached!
                  </div>
                )}
              </div>
            )}
          </div>
          <div style={cardsGridStyle} className="cards-grid">
            <div
              style={cardStyle(
                "linear-gradient(135deg, rgba(59, 130, 246, 0.08), rgba(37, 99, 235, 0.08))",
                "rgba(59, 130, 246, 0.2)"
              )}
              onMouseEnter={(e) => {
                e.target.style.transform = "translateY(-4px)";
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = "translateY(0)";
              }}
            >
              <div
                style={{
                  backgroundColor: "rgba(59, 130, 246, 0.2)",
                  borderRadius: "50%",
                  padding: popup ? "10px" : "12px",
                  width: "fit-content",
                  margin: `0 auto ${popup ? "12px" : "16px"}`,
                }}
              >
                <Icon
                  icon="mdi:collage"
                  width={popup ? "24" : "28"}
                  height={popup ? "24" : "28"}
                  color="#3b82f6"
                />
              </div>
              <h3
                style={{
                  color: "#1f2937",
                  fontWeight: "600",
                  marginBottom: "8px",
                  fontSize: popup ? "14px" : "16px",
                  margin: "0 0 8px 0",
                }}
              >
                Educational Information
              </h3>
              <p
                style={{
                  color: "#4b5563",
                  fontSize: popup ? "11px" : "12px",
                  margin: "0",
                  lineHeight: "1.4",
                }}
              >
                Your Educational Journey to help you with Application
              </p>
            </div>

            <div
              style={cardStyle(
                "linear-gradient(135deg, rgba(16, 185, 129, 0.08), rgba(5, 150, 105, 0.08))",
                "rgba(16, 185, 129, 0.2)"
              )}
              onMouseEnter={(e) => {
                e.target.style.transform = "translateY(-4px)";
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = "translateY(0)";
              }}
            >
              <div
                style={{
                  backgroundColor: "rgba(16, 185, 129, 0.2)",
                  borderRadius: "50%",
                  padding: popup ? "10px" : "12px",
                  width: "fit-content",
                  margin: `0 auto ${popup ? "12px" : "16px"}`,
                }}
              >
                <Icon
                  icon="mdi:clipboard-list"
                  width={popup ? "24" : "28"}
                  height={popup ? "24" : "28"}
                  color="#10b981"
                />
              </div>
              <h3
                style={{
                  color: "#1f2937",
                  fontWeight: "600",
                  marginBottom: "8px",
                  fontSize: popup ? "14px" : "16px",
                  margin: "0 0 8px 0",
                }}
              >
                Story & Achievements
              </h3>
              <p
                style={{
                  color: "#4b5563",
                  fontSize: popup ? "11px" : "12px",
                  margin: "0",
                  lineHeight: "1.4",
                }}
              >
                Share your story and achievements to stand out in applications
              </p>
            </div>

            <div
              style={cardStyle(
                "linear-gradient(135deg, rgba(245, 158, 11, 0.08), rgba(217, 119, 6, 0.08))",
                "rgba(245, 158, 11, 0.2)"
              )}
              onMouseEnter={(e) => {
                e.target.style.transform = "translateY(-4px)";
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = "translateY(0)";
              }}
            >
              <div
                style={{
                  backgroundColor: "rgba(245, 158, 11, 0.2)",
                  borderRadius: "50%",
                  padding: popup ? "10px" : "12px",
                  width: "fit-content",
                  margin: `0 auto ${popup ? "12px" : "16px"}`,
                }}
              >
                <Icon
                  icon="mdi:star"
                  width={popup ? "24" : "28"}
                  height={popup ? "24" : "28"}
                  color="#f59e0b"
                />
              </div>
              <h3
                style={{
                  color: "#1f2937",
                  fontWeight: "600",
                  marginBottom: "8px",
                  fontSize: popup ? "14px" : "16px",
                  margin: "0 0 8px 0",
                }}
              >
                Activity Tracking
              </h3>
              <p
                style={{
                  color: "#4b5563",
                  fontSize: popup ? "11px" : "12px",
                  margin: "0",
                  lineHeight: "1.4",
                }}
              >
                Document your extracurriculars and achievements for applications
              </p>
            </div>
          </div>
          {(education?.grade || test?.type || activities?.length > 0) && (
            <div style={infoSectionStyle}>
              <div style={infoGridStyle} className="info-grid">
                <div
                  style={infoCardStyle(
                    "rgba(239, 246, 255, 0.6)",
                    "rgba(147, 197, 253, 0.3)"
                  )}
                >
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: "20px",
                    }}
                  >
                    <div>
                      <h4
                        style={{
                          fontWeight: "600",
                          color: "#1e40af",
                          marginBottom: "12px",
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          margin: "0 0 12px 0",
                          fontSize: "14px",
                        }}
                      >
                        <Icon icon="mdi:collage" width="16" height="16" />
                        Education
                      </h4>
                      {education.grade && (
                        <div
                          style={{
                            color: "#374151",
                            fontSize: "12px",
                            marginBottom: "4px",
                          }}
                        >
                          Grade:{" "}
                          {education.grade.charAt(0).toUpperCase() +
                            education.grade.slice(1)}
                        </div>
                      )}
                      {education.gpa && (
                        <div
                          style={{
                            color: "#374151",
                            fontSize: "12px",
                            marginBottom: "4px",
                          }}
                        >
                          GPA: {education.gpa}
                        </div>
                      )}
                      {education.collage_name && (
                        <div
                          style={{
                            color: "#374151",
                            fontSize: "12px",
                            marginBottom: "4px",
                            wordBreak: "break-word",
                          }}
                        >
                          collage: {education.collage_name}
                        </div>
                      )}
                      {education.class_rank && (
                        <div style={{ color: "#374151", fontSize: "12px" }}>
                          Rank: {education.class_rank}
                        </div>
                      )}
                    </div>

                    <div>
                      <h4
                        style={{
                          fontWeight: "600",
                          color: "#1e40af",
                          marginBottom: "12px",
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          margin: "0 0 12px 0",
                          fontSize: "14px",
                        }}
                      >
                        <Icon
                          icon="mdi:file-document-edit"
                          width="16"
                          height="16"
                        />
                        Test Scores
                      </h4>
                      {test?.type && (
                        <div
                          style={{
                            color: "#374151",
                            fontSize: "12px",
                            marginBottom: "4px",
                          }}
                        >
                          Type: {test?.type?.toUpperCase()}
                        </div>
                      )}
                      {test?.total && (
                        <div
                          style={{
                            color: "#374151",
                            fontSize: "12px",
                            marginBottom: "4px",
                          }}
                        >
                          Total: {test?.total}
                        </div>
                      )}
                      {test?.math && (
                        <div
                          style={{
                            color: "#374151",
                            fontSize: "12px",
                            marginBottom: "4px",
                          }}
                        >
                          Math: {test?.math}
                        </div>
                      )}
                      {test?.reading_writing && (
                        <div
                          style={{
                            color: "#374151",
                            fontSize: "12px",
                            marginBottom: "4px",
                          }}
                        >
                          R&W: {test?.reading_writing}
                        </div>
                      )}
                      {test?.test_date && (
                        <div style={{ color: "#374151", fontSize: "12px" }}>
                          Date: {test?.test_date}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div
                  style={infoCardStyle(
                    "rgba(240, 253, 244, 0.6)",
                    "rgba(167, 243, 208, 0.3)"
                  )}
                >
                  <h4
                    style={{
                      fontWeight: "600",
                      color: "#065f46",
                      marginBottom: "12px",
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      margin: "0 0 12px 0",
                      fontSize: "14px",
                    }}
                  >
                    <Icon icon="mdi:book" width="16" height="16" />
                    Story Progress
                  </h4>

                  {storyQuestions.map((question, index) => {
                    const answer = story?.[question.key];
                    const isComplete = !!answer;
                    return (
                      <div
                        key={question.key}
                        style={{
                          color: isComplete ? "#065f46" : "#6b7280",
                          fontSize: "12px",
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          marginBottom: "6px",
                          padding: "4px 0",
                        }}
                      >
                        <span>{question.label}</span>
                        <span
                          style={{
                            fontSize: "14px",
                            fontWeight: "500",
                            color: isComplete ? "green" : "#ccc",
                          }}
                        >
                          ✔
                        </span>
                      </div>
                    );
                  })}
                </div>

                <div
                  style={infoCardStyle(
                    "rgba(255, 251, 235, 0.6)",
                    "rgba(252, 211, 77, 0.3)"
                  )}
                >
                  <h4
                    style={{
                      fontWeight: "600",
                      color: "#92400e",
                      marginBottom: "12px",
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      margin: "0 0 12px 0",
                      fontSize: "14px",
                    }}
                  >
                    <Icon icon="mdi:star" width="16" height="16" />
                    Activities ({activities.length})
                  </h4>
                  {activities.slice(0, 3).map((activity, index) => (
                    <div
                      key={index}
                      style={{
                        color: "#374151",
                        fontSize: "12px",
                        marginBottom: "4px",
                        padding: "2px 0",
                        wordBreak: "break-word",
                      }}
                    >
                      {activity.name} - {activity?.grades}
                    </div>
                  ))}
                  {activities.length > 3 && (
                    <div
                      style={{
                        color: "#6b7280",
                        fontSize: "12px",
                        fontStyle: "italic",
                        marginTop: "4px",
                      }}
                    >
                      +{activities.length - 3} more activities
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      {showModal && (
        <PricingModal
          show={true}
          onClose={() => {
            history("/journey");
            setShowModal(false);
          }}
          setShowModel={() => {
            history("/journey");
            setShowModal(false);
          }}
          backdrop="static"
          keyboard={false}
          onHide={() => {
            history("/journey");
            setShowModal(false);
          }}
          onCloseHitApi={() => {
            loadUserData();
            disconnect();
            setShowModal(false);
            dispatch(user_profile_info("", tokenSelector));
          }}
          source={"agent"}
        />
      )}
    </>
  );
}
